import React from "react";
import { Mail, FolderOpen, FileText, Users, BarChart3, PieChart } from "lucide-react";
import { MainLayout } from "../ui/components";
import { ActiveView, UserProfile } from "../../types/objects";

interface IPortfolioProps {
	user: UserProfile;
	/** Switches the dashboard to another module. */
	onNavigate?: (view: ActiveView) => void;
}

const MODULES: Array<{ key: ActiveView; title: string; text: string; icon: React.ReactNode }> = [
	{ key: "messages", title: "Zprávy", text: "Zprávy z kontaktního formuláře webu.", icon: <Mail size={24} /> },
	{ key: "contacts", title: "Kontakty", text: "Subjekty v CRM a jejich hodnocení.", icon: <Users size={24} /> },
	{ key: "scoring", title: "Screening", text: "Pravidlové hodnocení dokumentů, včetně vzorových scénářů.", icon: <BarChart3 size={24} /> },
	{ key: "documents", title: "Dokumenty", text: "Interní podklady ke klientům.", icon: <FolderOpen size={24} /> },
	{ key: "contracts", title: "Smlouvy", text: "Uzavřené smlouvy a přílohy.", icon: <FileText size={24} /> },
	{ key: "reports", title: "Reporty", text: "Vývoj akvizice a struktura kontaktů.", icon: <PieChart size={24} /> },
];

/**
 * IPortfolio — landing page of the internal part: welcome, module shortcuts and team overview.
 */
const IPortfolio: React.FC<IPortfolioProps> = ({ user, onNavigate }) => (
	<MainLayout title="Interní portfolio" subtitle={`Vítejte zpět, ${user.username}! Zde najdete zprávy, kontakty, dokumenty a nástroje pro hodnocení klientů.`}>
		<div className="space-y-10">
			<section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" aria-label="Moduly CRM">
				{MODULES.map((m) => (
					<button
						key={m.key}
						type="button"
						onClick={() => onNavigate?.(m.key)}
						className="text-left p-6 rounded-3xl border border-slate-200 bg-white hover:border-blue-400 hover:shadow-lg transition-all group"
					>
						<div className="p-3 bg-blue-50 text-blue-600 rounded-2xl w-fit mb-4 group-hover:bg-blue-600 group-hover:text-white transition-all">{m.icon}</div>
						<div className="font-black text-slate-900 text-lg">{m.title}</div>
						<div className="text-sm text-slate-500 mt-1">{m.text}</div>
					</button>
				))}
			</section>

			<section className="bg-gradient-to-br from-sky-50 to-sky-100 border border-sky-200 rounded-3xl p-8 space-y-4 text-slate-700 text-lg leading-relaxed">
				<p>
					Zaměřujeme se na <strong>dohled</strong> nad firemními procesy, zajištění jejich správné funkčnosti
					a transparentnosti pro všechny zúčastněné strany.
				</p>
				<p>
					Naším cílem je efektivní <strong>integrace</strong> systémů a nástrojů, které naši klienti používají –
					plynulý tok dat, automatizace úkonů a přehled nad celým firemním prostředím.
				</p>
				<p>
					Provádíme <strong>optimalizaci procesů</strong> na základě datové analýzy a zpětné vazby od klienta
					a <strong>rating procesů</strong> – hodnotíme jejich výkonnost, stabilitu a návratnost.
				</p>
				<p>
					Náš tým: <strong>Martin</strong> se stará o komunikaci s klienty, <strong>Luboš</strong> má na starosti
					finance a vedení firmy jako ředitel a <strong>Michal</strong> se věnuje technickým návrhům, jejich ověřování
					a realizaci PoC (Proof of Concept).
				</p>
			</section>
		</div>
	</MainLayout>
);

export default IPortfolio;
